import * as THREE from "three";
import type {
  MaterialId,
  RaidEnv,
  ScavRole,
  TimeOfDay,
  WildlifeSpecies,
} from "@rietz/shared";

/**
 * The single source of colour truth for the renderer. Two rules: nothing else
 * may invent a colour (a re-grade is a one-file edit), and all variation is
 * hash-driven, never `Math.random` (two clients must see identical colours).
 * Intent: alpine Tirol in late-morning light — warm larch and ochre against
 * cool gneiss, desaturated so gameplay colours read against it.
 */

// ---------------------------------------------------------------------------
// Deterministic hashing
// ---------------------------------------------------------------------------

/** 32-bit integer mix (murmur3 finaliser flavour). Stable across engines. */
export function hash32(a: number, b = 0): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h ^ Math.imul(b + 0x165667b1, 0x27d4eb2f), 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** FNV-1a over a string — used for per-player tints and prop-kind salts. */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic pseudo-random in [0, 1). */
export function rand01(a: number, b = 0): number {
  return hash32(a, b) / 4294967296;
}

/** Deterministic pseudo-random in [-1, 1). */
export function randSym(a: number, b = 0): number {
  return rand01(a, b) * 2 - 1;
}

// ---------------------------------------------------------------------------
// Sky, fog, light
// ---------------------------------------------------------------------------

/** Straight up: deep alpine blue. */
export const SKY_ZENITH = 0x3d6fae;
/** The band the mountains sit in: pale valley haze. */
export const SKY_HORIZON = 0xc3d4e2;
/** Slightly warmer than the horizon so distance reads as haze, not as grey. */
export const FOG_COLOR = 0xbccddd;
/**
 * Exp² fog density: ~20 % haze at 100 m, 60 % at 200 m — village sightlines
 * usable, the mountain ring reads as distant.
 */
export const FOG_DENSITY = 0.0055;

/** Kept for compatibility: the clear colour is the sky's horizon band. */
export const SKY_COLOR = SKY_HORIZON;

export const SUN_COLOR = 0xfff3dc;
export const SUN_INTENSITY = 1.2;
/** From the south-west and high, i.e. late morning over the valley. */
export const SUN_DIR = { x: -0.55, y: 0.72, z: 0.42 } as const;

export const HEMI_SKY_COLOR = 0xb9cfe6;
export const HEMI_GROUND_COLOR = 0x6a6150;
/** Deliberately strong: there are no shadows this phase, so ambient must
 *  carry interiors. Too low and every hut is a black hole. */
export const HEMI_INTENSITY = 0.95;

/** A cool counter-light so north faces are shaded, not black. */
export const FILL_COLOR = 0x9fb6cc;
export const FILL_INTENSITY = 0.26;
export const FILL_DIR = { x: 0.6, y: 0.35, z: -0.72 } as const;

// ---------------------------------------------------------------------------
// Lighting presets. The constants above are the DAY-CLEAR preset, and
// `LIGHTING_DAY` is built from them. A preset is twelve numbers pushed into
// three lights, fog and sky — never a switch on materials or geometry, which
// is why a raid can change its sky without rebuilding a mesh. Weather is
// applied on top of the hour as a modifier.
// ---------------------------------------------------------------------------

/** Everything the view needs to light one raid. See `lightingFor`. */
export interface LightingPreset {
  /** Straight up, and the band the mountains stand in. */
  skyZenith: number;
  skyHorizon: number;
  /** Exponential-squared fog: the colour distance dissolves into, and how fast. */
  fogColor: number;
  fogDensity: number;
  /** The key light. `sunDir` points FROM the ground TOWARDS the sun. */
  sunColor: number;
  sunIntensity: number;
  sunDir: { x: number; y: number; z: number };
  /** The ambient floor — sky above, bounced ground below. */
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  /**
   * The cool counter-light. Its direction is `FILL_DIR` in every preset — a
   * fill exists so north faces are not black.
   */
  fillColor: number;
  fillIntensity: number;
}

/** Late-morning Tirol — the light the game was authored under. */
const LIGHTING_DAY: LightingPreset = {
  skyZenith: SKY_ZENITH,
  skyHorizon: SKY_HORIZON,
  fogColor: FOG_COLOR,
  fogDensity: FOG_DENSITY,
  sunColor: SUN_COLOR,
  sunIntensity: SUN_INTENSITY,
  sunDir: { x: SUN_DIR.x, y: SUN_DIR.y, z: SUN_DIR.z },
  hemiSky: HEMI_SKY_COLOR,
  hemiGround: HEMI_GROUND_COLOR,
  hemiIntensity: HEMI_INTENSITY,
  fillColor: FILL_COLOR,
  fillIntensity: FILL_INTENSITY,
};

/**
 * First light from the east: sun low (y 0.25) and a third dimmer, the
 * hemisphere holding what it lost — or it reads as an eclipse.
 */
const LIGHTING_DAWN: LightingPreset = {
  skyZenith: 0x2b4a7d,
  skyHorizon: 0xf0b489,
  fogColor: 0xdcae92,
  fogDensity: 0.0062,
  sunColor: 0xffd2a1,
  sunIntensity: 0.8,
  sunDir: { x: 0.82, y: 0.25, z: 0.32 },
  hemiSky: 0x9fb2d6,
  hemiGround: 0x5e5142,
  hemiIntensity: 0.7,
  fillColor: 0x8fa4c6,
  fillIntensity: 0.22,
};

/** The last half hour from the west: dawn mirrored and a few degrees redder. */
const LIGHTING_DUSK: LightingPreset = {
  skyZenith: 0x27386b,
  skyHorizon: 0xee9a79,
  fogColor: 0xd2988a,
  fogDensity: 0.0065,
  sunColor: 0xffbe8c,
  sunIntensity: 0.8,
  sunDir: { x: -0.84, y: 0.25, z: -0.28 },
  hemiSky: 0x94a3cc,
  hemiGround: 0x574a3f,
  hemiIntensity: 0.7,
  fillColor: 0x8496bd,
  fillIntensity: 0.22,
};

/**
 * A clear, moonlit night. The "sun" is the moon at 0.08 — a rim on a roof
 * ridge; everything navigable comes from the hemisphere at 0.18 (a raid lit
 * at zero is a black screen with gunfire in it). Baked AO still darkens
 * interiors. The fog is the DARKEST colour here — at night distance swallows.
 */
const LIGHTING_NIGHT: LightingPreset = {
  skyZenith: 0x05070f,
  skyHorizon: 0x141c31,
  fogColor: 0x0c1220,
  fogDensity: 0.0062,
  sunColor: 0xa8bde6,
  sunIntensity: 0.08,
  sunDir: { x: 0.34, y: 0.78, z: -0.35 },
  hemiSky: 0x5c6d96,
  hemiGround: 0x2c2f38,
  hemiIntensity: 0.18,
  fillColor: 0x6f81a4,
  fillIntensity: 0.12,
};

const LIGHTING_BY_TIME: Record<TimeOfDay, LightingPreset> = {
  dawn: LIGHTING_DAWN,
  day: LIGHTING_DAY,
  dusk: LIGHTING_DUSK,
  night: LIGHTING_NIGHT,
};

/** Fog multiplies the density it found by this. Three is the difference
 *  between "hazy" and "you can see the next house and not the one after". */
const FOG_DENSITY_MULT = 3;
/** ...and takes this much off the sun, which is scattered rather than blocked. */
const FOG_SUN_MULT = 0.6;
/** How far the zenith is dragged down to the fog colour. Not all the way: a
 *  dome that is one flat colour reads as a bug, even in fog. */
const FOG_ZENITH_MIX = 0.6;

/** Rain is thinner than fog but still cuts the distance down. */
const RAIN_DENSITY_MULT = 1.6;
const RAIN_SUN_MULT = 0.7;
/** What is left of a colour's saturation under rain — an overcast sky is the
 *  one sky with no hue in it, and the fog under it borrows that. */
const RAIN_SATURATION = 0.35;
/**
 * ...and a touch of lightness, so the grey is rain grey, not paper. Sky and fog
 * only; the lights keep their hue.
 */
const RAIN_LIGHTNESS = 0.88;

/**
 * Snow is thicker than rain and thinner than fog (1.7 / 1.6 / 3) — the same
 * ordering `lightFactor` gives a scav's eyes in `shared/sim/env.ts`, so what
 * the player sees and what the AI sees move together.
 */
const SNOW_DENSITY_MULT = 1.7;
/** ...and takes the same third off the sun that rain does. A snow sky is one
 *  flat overcast: there is no key light left in it, only a bright ceiling. */
const SNOW_SUN_MULT = 0.7;
/**
 * The pale blue-grey the sky and fog are dragged toward — not white, or the
 * horizon is a blown-out hole.
 */
const SNOW_SKY_COLOR = 0xe9eef3;
/** How far the fog (and with it the horizon, which must equal it) goes there. */
const SNOW_FOG_MIX = 0.55;
/**
 * ...and the zenith, less far: straight up still has a sky's colour. Applied
 * over `overcast`.
 */
const SNOW_ZENITH_MIX = 0.35;
/**
 * Snow bounces light up: the hemisphere's ground half becomes brighter than
 * soil ever was, lighting every eave from below.
 */
const SNOW_BOUNCE_COLOR = 0xd9e2ea;
const SNOW_BOUNCE_MIX = 0.6;

const tmpLightHSL = { h: 0, s: 0, l: 0 };
const tmpLightColor = new THREE.Color();
const tmpMixColor = new THREE.Color();

/** Pulls the saturation (and a little of the lightness) out of one colour. */
function overcast(hex: number): number {
  tmpLightColor.setHex(hex);
  tmpLightColor.getHSL(tmpLightHSL);
  return tmpLightColor
    .setHSL(tmpLightHSL.h, tmpLightHSL.s * RAIN_SATURATION, tmpLightHSL.l * RAIN_LIGHTNESS)
    .getHex();
}

/** `a` blended `t` of the way towards `b`. */
function mixHex(a: number, b: number, t: number): number {
  tmpLightColor.setHex(a);
  tmpMixColor.setHex(b);
  return tmpLightColor.lerp(tmpMixColor, t).getHex();
}

/**
 * The lighting this raid is drawn under: the hour with the weather laid over
 * it. A fresh object per call; once per raid.
 */
export function lightingFor(env: RaidEnv): LightingPreset {
  const base = LIGHTING_BY_TIME[env.time];
  const p: LightingPreset = { ...base, sunDir: { ...base.sunDir } };

  if (env.weather === "fog") {
    p.fogDensity = base.fogDensity * FOG_DENSITY_MULT;
    // The horizon IS the fog: geometry fading at the skyline must fade into
    // the dome's colour or the map ends in a seam.
    p.skyHorizon = base.fogColor;
    p.skyZenith = mixHex(base.skyZenith, base.fogColor, FOG_ZENITH_MIX);
    p.sunIntensity = base.sunIntensity * FOG_SUN_MULT;
  } else if (env.weather === "rain") {
    p.fogDensity = base.fogDensity * RAIN_DENSITY_MULT;
    p.skyZenith = overcast(base.skyZenith);
    p.skyHorizon = overcast(base.skyHorizon);
    p.fogColor = overcast(base.fogColor);
    p.sunIntensity = base.sunIntensity * RAIN_SUN_MULT;
  } else if (env.weather === "snow") {
    p.fogDensity = base.fogDensity * SNOW_DENSITY_MULT;
    p.fogColor = mixHex(base.fogColor, SNOW_SKY_COLOR, SNOW_FOG_MIX);
    // Same rule — and against a pale sky a seam is far more visible.
    p.skyHorizon = p.fogColor;
    p.skyZenith = mixHex(overcast(base.skyZenith), SNOW_SKY_COLOR, SNOW_ZENITH_MIX);
    p.sunIntensity = base.sunIntensity * SNOW_SUN_MULT;
    p.hemiGround = mixHex(base.hemiGround, SNOW_BOUNCE_COLOR, SNOW_BOUNCE_MIX);
  }

  return p;
}

// ---------------------------------------------------------------------------
// Snow on the ground (protocol v29)
// ---------------------------------------------------------------------------

/**
 * What lying snow does to `mapMesh.ts`'s vertex colours: a coat, not a repaint
 * — lerped, so the per-cell jitter survives compressed.
 */
export const SNOW_GROUND_COLOR = 0xe4eaef;

/**
 * ...and on the TOPS of solids, a touch bluer and brighter. Sides are left
 * alone — snow does not stick to walls and whitening them erases the material
 * read.
 */
export const SNOW_ROOF_COLOR = 0xe8edf1;

/** How far a top face goes towards `SNOW_ROOF_COLOR`. Short of the ground's,
 *  so a roof still says what it is made of at the eaves. */
export const SNOW_ROOF_MIX = 0.7;

/**
 * How far each terrain material goes toward `SNOW_GROUND_COLOR`. A `Record`
 * so a new `MaterialId` is a compile error. Trodden (concrete, metal — the
 * street becomes slush) vs untrodden (everything else takes the full coat).
 */
export const SNOW_GROUND_MIX: Record<MaterialId, number> = {
  concrete: 0.5,
  metal: 0.5,
  wood: 0.82,
  dirt: 0.82,
  brick: 0.82,
  stone: 0.82,
  // Panes are vertical and never ground: no snow on them.
  glass: 0,
};

// ---------------------------------------------------------------------------
// Solid materials
// ---------------------------------------------------------------------------

export const MATERIAL_COLORS: Record<MaterialId, number> = {
  concrete: 0x9b9a93,
  wood: 0x8a6b47,
  metal: 0x6e7681,
  dirt: 0x7a6a52,
  brick: 0x9e5f45,
  stone: 0x848778,
  // Panes are drawn by `render/glass.ts`, never by the map mesh.
  glass: 0xcfe3ee,
};

/** Per-material hue/saturation/lightness jitter amplitudes (±, HSL units). */
export interface Jitter {
  h: number;
  s: number;
  l: number;
}

/**
 * Rough materials vary more than manufactured ones; variation on metal reads as
 * dirt.
 */
export const MATERIAL_JITTER: Record<MaterialId, Jitter> = {
  concrete: { h: 0.008, s: 0.02, l: 0.045 },
  wood: { h: 0.014, s: 0.05, l: 0.06 },
  metal: { h: 0.006, s: 0.02, l: 0.03 },
  dirt: { h: 0.018, s: 0.06, l: 0.07 },
  brick: { h: 0.012, s: 0.05, l: 0.055 },
  stone: { h: 0.012, s: 0.035, l: 0.06 },
  glass: { h: 0, s: 0, l: 0 },
};

// ---------------------------------------------------------------------------
// Baked ambient occlusion (vertex-colour only — no lightmaps this phase)
// ---------------------------------------------------------------------------

/** Multiplier for upward faces: full light. */
export const AO_TOP = 1.0;
/** Multiplier for vertical faces. */
export const AO_SIDE = 0.85;
/** Multiplier for downward faces — undersides read as "not sky-lit". */
export const AO_BOTTOM = 0.62;
/** Per-face random spread added to `AO_SIDE`, ±. */
export const AO_SIDE_VARIATION = 0.045;
/** Side faces are darkened toward this factor as they approach the ground… */
export const AO_GROUND_FACTOR = 0.74;
/** …over this height, in metres. Grounds objects without a contact shadow. */
export const AO_GROUND_HEIGHT = 0.5;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export const PINE_TRUNK_COLOR = 0x4a3729;
/** Base of the canopy: deep shadowed spruce. */
export const PINE_NEEDLE_DARK = 0x2c4a33;
/** Tips: sun-caught new growth. */
export const PINE_NEEDLE_LIGHT = 0x4a7346;
/** Village linden/maple bark: greyer and lighter than a spruce's. */
export const BROADLEAF_TRUNK_COLOR = 0x6b5540;
/** Underside of the crown, where the leaves shade each other. */
export const BROADLEAF_CANOPY_DARK = 0x3a5730;
/** Sun-side leaves: warmer and lighter than any conifer in the valley. */
export const BROADLEAF_CANOPY_LIGHT = 0x6f8f45;
/** Orchard apple bark — young, thin, still reddish under the grey. */
export const FRUIT_TRUNK_COLOR = 0x5d4630;
export const FRUIT_CANOPY_DARK = 0x47602c;
/** A touch yellower than the lindens, so an orchard reads as its own block. */
export const FRUIT_CANOPY_LIGHT = 0x86a04a;
export const GRASS_BASE_COLOR = 0x5f7a3c;
export const GRASS_TIP_COLOR = 0x93a154;
export const ROCK_COLOR = 0x8b8d84;
export const FLOWERBOX_WOOD = 0x7a5738;
/** Geranium red, alpine-rose pink, and the odd white — Tirol balcony canon. */
export const FLOWER_COLORS = [0xc23a34, 0xd8768f, 0xe4ded2, 0xcf4f6a] as const;

// ---------------------------------------------------------------------------
// Facades (render/props.ts): windows and doors hang ON a wall, so they are
// graded against the plaster — cream joinery that reads at 60 m, glass dark
// enough that a facade is not a row of holes.
// ---------------------------------------------------------------------------

/** Painted window joinery — cream, the standard on ochre Tirol plaster. */
export const WINDOW_FRAME = 0xefe9dc;
/** Bottom of a pane: the shaded room behind it. */
export const WINDOW_GLASS_DARK = 0x2e3a46;
/** Top of a pane: a hint of the sky it reflects. */
export const WINDOW_GLASS_LIGHT = 0x5a6f84;
/** Cast-stone sill — greyer and dirtier than the frame above it. */
export const WINDOW_SILL = 0xcfc7b8;
/** BREAKABLE glazing (`render/glass.ts`), lit and transparent — not the flat
 *  gradient a decorative `window` prop is painted with. */
export const GLASS_PANE_COLOR = 0xcfe3ee;
/** Louvred shutters, in the dark green every second house in the valley uses. */
export const SHUTTER_COLOR = 0x4b6b46;

/** Bottom of a door leaf, where the oiled larch has darkened with rain. */
export const DOOR_WOOD_DARK = 0x4a3322;
/** The leaf's sunlit upper half. */
export const DOOR_WOOD = 0x6a4a30;
/** Jambs and head: lighter sawn timber framing the leaf. */
export const DOOR_FRAME = 0x8a6a48;
/** The stone step at the threshold — the same gneiss as the rocks. */
export const DOOR_STEP = 0x8b8880;

// ---------------------------------------------------------------------------
// Signposts (render/signs.ts): cold enamel on galvanised steel vs warm larch
// on a warm post — kind should read from across the square.
// ---------------------------------------------------------------------------

/** Galvanised steel pole under a street plate. */
export const SIGN_POST_METAL = 0x8f959b;
/** Squared larch post under a place board — the same wood as the flowerboxes. */
export const SIGN_POST_WOOD = 0x74563a;

/** RAL 5017-ish traffic blue: the enamel on every Ortstafel in the Tirol. */
export const STREET_PLATE_COLOR = 0x1d4288;
/** The hairline border and the lettering — plain enamel white. */
export const STREET_PLATE_INK = 0xf4f6f8;
/** Edge-on, a plate is bare metal folded over: lighter than its face. */
export const STREET_PLATE_EDGE = 0xb9bec3;

/** Oiled larch plank. */
export const POI_BOARD_COLOR = 0x6d4f31;
/** Weathered sawn edge — a shade greyer and darker than the face. */
export const POI_BOARD_EDGE = 0x503a24;
/** Carved-and-painted lettering: cream, never white, on wood. */
export const POI_BOARD_INK = 0xf0e6d0;
/** Grain streaks drawn over the board face. */
export const POI_BOARD_GRAIN = 0x5b4028;

// ---------------------------------------------------------------------------
// Gameplay volumes
// ---------------------------------------------------------------------------

export const EXTRACT_FILL_COLOR = 0x4fe08a;
export const EXTRACT_EDGE_COLOR = 0x9dffc4;

/** Grid fallback for maps with no authored props (Testfield). */
export const GRID_COLOR = 0x53607a;

// ---------------------------------------------------------------------------
// Characters
// ---------------------------------------------------------------------------

/** Indexed by a hash of the entity id, so a player keeps one colour forever. */
export const PLAYER_TINTS = [
  0xd8695a, 0x5a9ad8, 0x7cbb64, 0xd8ac57, 0xa87cd8, 0x54c9b6, 0xd85f95,
  0x98a1ab,
] as const;

export const SKIN_COLOR = 0xc79878;
export const GEAR_DARK = 0x3a3f45;
export const GEAR_MID = 0x4e555d;

/**
 * The gloved hands on the first-person gun: darker than any gun part, so they
 * never pull the eye off the sights. Remote hands use the rig's webbing tone.
 */
export const GLOVE_COLOR = 0x24262a;
/** The cuff behind the glove — a shade lighter, so wrist and fist separate. */
export const SLEEVE_COLOR = 0x3b4048;

/**
 * Worn armour overlay: desaturated and darker than every player tint, so
 * "armoured" never competes with "which player".
 */
export const GEAR_VEST_COLOR = 0x565c52;
export const GEAR_HELMET_COLOR = 0x4a4f48;

export function playerTintFor(id: string): number {
  const idx = hashString(id) % PLAYER_TINTS.length;
  return PLAYER_TINTS[idx] ?? PLAYER_TINTS[0];
}

/**
 * Wilderer, in loden and grey-brown: five muted tones no player tint comes
 * near, so "green-brown and dull" is a reliable read before the tag is legible.
 * Hashed by entity id like the PMC tints.
 */
export const SCAV_TINTS = [
  /** Loden green — the jacket. */ 0x54613f,
  /** Wet moss. */ 0x46543c,
  /** Faded olive drab. */ 0x6a6b4a,
  /** Grey-brown, the colour of an old wool coat. */ 0x6a5f4e,
  /** Bark. */ 0x574a3a,
] as const;

/** Trousers, belt and pack on a scav: darker earth than the PMC's gear grey. */
export const SCAV_GEAR_COLOR = 0x3b342a;
/** Their cap brim and small accents — a shade lighter, so the facing cue reads. */
export const SCAV_ACCENT_COLOR = 0x5b5140;

export function scavTintFor(id: string): number {
  const idx = hashString(id) % SCAV_TINTS.length;
  return SCAV_TINTS[idx] ?? SCAV_TINTS[0];
}

// ---------------------------------------------------------------------------
// Scav roles. The read happens at 60 m through haze off a few pixels, which
// rules out everything but colour — and fog eats hue first, so roles are
// separated by VALUE and GEAR TONE, from each other and from the Wilderer:
//   wilderer   mid green-brown, earth gear (SCAV_TINTS)
//   sniper     bleached grey-brown, same gear
//   raider     olive drab, BLACK gear
//   guard      slate grey, muted red accent
//   boss       near-black, one blood-red note
// Uniformed roles get one tone each, no per-entity hash: four raiders who are
// visibly one outfit is the information. The Wilderer stays hashed.
// ---------------------------------------------------------------------------

/**
 * The Hunter's coat: loden so dark it is nearly black — the only silhouette
 * darker than a spruce's shaded side, a hole in the hillside shaped like a man.
 */
export const BOSS_TINT = 0x232a20;
/** His webbing, blacker still than the coat: the plate carrier is the best
 *  armour in the game and it is not meant to look like a poacher's belt. */
export const BOSS_GEAR_COLOR = 0x141513;
/**
 * The Gamsbart brim — the one saturated note on any scav. Deep arterial red,
 * never the laser's 0xff2018.
 */
export const BOSS_ACCENT_COLOR = 0x8c1f1a;

/**
 * The Hunter's men: dark slate grey, the only role with no green or brown —
 * gone against stone, visible against the meadow he watches.
 */
export const GUARD_TINT = 0x3e444a;
/** Darker slate for the belt and pack: one family, two values, so the kit
 *  separates from the coat at a range where neither has a hue left. */
export const GUARD_GEAR_COLOR = 0x272b30;
/**
 * The armband: the boss's red with the life taken out, so "those are HIS men"
 * reads without a guard being mistaken for the man.
 */
export const GUARD_ACCENT_COLOR = 0x6f3f3c;

/**
 * The raider: olive drab, flatter and darker than the Wilderer's — factory-dyed
 * rather than weathered. The gear is the real cue.
 */
export const RAIDER_TINT = 0x596033;
/**
 * Black nylon — THE role cue: everyone else carries brown leather or earth
 * canvas.
 */
export const RAIDER_GEAR_COLOR = 0x1c1e20;
/** Gunmetal for the small accents — a shade off the webbing, so the facing cue
 *  survives against a black rig the way the scav's does against a brown one. */
export const RAIDER_ACCENT_COLOR = 0x32363a;

/**
 * The marksman: mottled grey-brown, LIGHTER than any other scav — the value
 * that reads against a dark hedge at 400 m through a scope.
 */
export const SNIPER_TINT = 0x7b7261;
/**
 * A marksman is a Wilderer who can shoot: the same earth webbing. Named so the
 * role table stays one flat lookup.
 */
export const SNIPER_GEAR_COLOR = SCAV_GEAR_COLOR;
export const SNIPER_ACCENT_COLOR = SCAV_ACCENT_COLOR;

/** The two tones a rig paints its webbing and its small accents with. */
export interface GearTones {
  gear: number;
  accent: number;
}

/**
 * Body tint per role, exhaustive over every role but the hashed Wilderer — a
 * sixth role is a compile error.
 */
const ROLE_TINTS: Record<Exclude<ScavRole, "wilderer">, number> = {
  raider: RAIDER_TINT,
  sniper: SNIPER_TINT,
  guard: GUARD_TINT,
  boss: BOSS_TINT,
};

/** Webbing and accent per role. Exhaustive over ALL of them: the Wilderer's
 *  gear is a constant even though his coat is not. */
const ROLE_GEAR: Record<ScavRole, GearTones> = {
  wilderer: { gear: SCAV_GEAR_COLOR, accent: SCAV_ACCENT_COLOR },
  raider: { gear: RAIDER_GEAR_COLOR, accent: RAIDER_ACCENT_COLOR },
  sniper: { gear: SNIPER_GEAR_COLOR, accent: SNIPER_ACCENT_COLOR },
  guard: { gear: GUARD_GEAR_COLOR, accent: GUARD_ACCENT_COLOR },
  boss: { gear: BOSS_GEAR_COLOR, accent: BOSS_ACCENT_COLOR },
};

/**
 * The body tint for one scav of `role`. `undefined` is the Wilderer (the wire
 * omits the field), hashed by `scavTintFor`.
 */
export function roleTintFor(id: string, role?: ScavRole): number {
  if (role === undefined || role === "wilderer") return scavTintFor(id);
  return ROLE_TINTS[role];
}

/**
 * Webbing and accent for one scav of `role`; `undefined` is the Wilderer's.
 * Returns a SHARED object — do not write to it.
 */
export function roleGearFor(role?: ScavRole): GearTones {
  return ROLE_GEAR[role ?? "wilderer"];
}

/** `0x8fe3b0` → `"#8fe3b0"`. Anything drawn on a canvas (the name tag) needs
 *  the CSS form of a colour this module states as a number. */
export function cssHex(hex: number): string {
  return `#${(hex >>> 0).toString(16).padStart(6, "0")}`;
}

const BOSS_ACCENT_CSS = cssHex(BOSS_ACCENT_COLOR);
const GUARD_ACCENT_CSS = cssHex(GUARD_ACCENT_COLOR);

/**
 * The ink a role's name tag is lettered in, or `undefined` for off-white. Only
 * the boss and his men get one — the coats' sentence said again at reading
 * range.
 */
export function roleTagInkFor(role?: ScavRole): string | undefined {
  if (role === "boss") return BOSS_ACCENT_CSS;
  if (role === "guard") return GUARD_ACCENT_CSS;
  return undefined;
}

// ---------------------------------------------------------------------------
// Loose loot: one colour per item KIND. A box at 15 m is three pixels, and the
// question it answers is "worth walking over to?".
// ---------------------------------------------------------------------------

/** Cartridge brass. */
export const ITEM_COLOR_AMMO = 0xb08a3c;
/** Gauze white — the one bright item colour, because meds are found under
 *  pressure and a bandage you cannot spot is a bandage you do not use. */
export const ITEM_COLOR_MED = 0xe3ded2;
/** Blued steel, matching the weapon palette below. */
export const ITEM_COLOR_WEAPON = 0x30343a;
/** Plate-carrier grey. */
export const ITEM_COLOR_ARMOR = 0x6b7078;
/** Rucksack canvas. */
export const ITEM_COLOR_CONTAINER = 0x6d5c44;
/** Barter goods: sacking and cardboard. */
export const ITEM_COLOR_LOOT = 0xa8905f;

// ---------------------------------------------------------------------------
// Weapons: a hunting carbine, not a military rifle — oiled walnut and blued
// steel.
// ---------------------------------------------------------------------------

export const WEAPON_STOCK_COLOR = 0x6b4526;
export const WEAPON_RECEIVER_COLOR = 0x2f3338;
export const WEAPON_BARREL_COLOR = 0x24272b;
/** Sights are the one bright part: they must be findable against any backdrop. */
export const WEAPON_SIGHT_COLOR = 0x8d949c;
/**
 * Front-sight fiber-optic bead, rendered unlit so the mark never disappears in
 * a doorway.
 */
export const WEAPON_SIGHT_BEAD_COLOR = 0xff6a2e;
/**
 * A magnifying scope's ocular at rest: coated glass from behind, nearly black
 * with a hint of green. The viewmodel swaps in the live picture; remote rifles
 * show only this.
 */
export const WEAPON_LENS_IDLE_COLOR = 0x101812;

// ---------------------------------------------------------------------------
// Combat effects. Everything here is additive, so "fade out" means "fade to
// black" and alpha never enters into it — see `game/fx.ts`.
// ---------------------------------------------------------------------------

/** The burning round itself — a bolt in flight, drawn unlit and unfogged. */
export const TRACER_HEAD_COLOR = 0xfff0c4;
export const MUZZLE_FLASH_COLOR = 0xffe2a0;
/** Warm point light thrown on the gun for the two frames a flash lasts. */
export const MUZZLE_LIGHT_COLOR = 0xffcf80;
/** Pulverised plaster and stone — deliberately colourless. */
export const IMPACT_DUST_COLOR = 0xcfc7ba;

// --- Grenades (protocol v24) ------------------------------------------------

/** The heart of a blast: hotter and whiter than a muzzle flash, and bigger. */
export const EXPLOSION_CORE_COLOR = 0xfff4d8;
/** The fragments thrown off it, drawn as additive sparks. */
export const EXPLOSION_SPARK_COLOR = 0xffb454;
/** The light one throws on the street for a quarter of a second. */
export const EXPLOSION_LIGHT_COLOR = 0xffb060;
/**
 * A smoke cloud: a cool blue-grey, unlike the dust puff's warm one — dust is
 * what a wall gives up, smoke is a chemical.
 */
export const SMOKE_COLOR = 0xb9bec4;

/**
 * The mounted torch: an incandescent bulb, warmer than `SUN_COLOR` so a lit
 * wall reads as "somebody is pointing a torch at it".
 */
export const TAC_LAMP_COLOR = 0xfff2d8;
/**
 * The laser: a red nothing else in the game is, so a line in the dark is never
 * mistaken for a tracer.
 */
export const TAC_LASER_COLOR = 0xff2018;

/**
 * Squadmates: name tag text and the diamond. A pale mint — the one colour in
 * the valley that cannot be mistaken for anything in it. Bright enough to hold
 * up unlit (both uses draw with no lighting).
 */
export const TEAM_COLOR = 0x8fe3b0;

// ---------------------------------------------------------------------------
// Wildlife (protocol v28)
// ---------------------------------------------------------------------------

/**
 * One animal's four tones — a single flat brown is a brown box; the white rump
 * and the pale mask are what read at 60 m.
 */
export interface AnimalCoat {
  /** The back, flanks, neck and legs. */
  coat: number;
  /** Belly, muzzle and — on the deer — the rump patch. The COUNTER-shade, and
   *  always much lighter than `coat`: it is the whole silhouette read. */
  belly: number;
  /** Antler, horn and hoof. Keratin, so never the coat colour. */
  horn: number;
  /** The face mask, ear linings and eye. */
  face: number;
}

/**
 * The three coats, chosen AGAINST the terrain: the hare a grey-brown against
 * stubble, the chamois near-black against rock — animals you have to find. The
 * deer is the legible exception: it lives where the light is worst, it is the
 * first quest, and its flight puts birds up, so it must be seen leaving.
 */
export const WILDLIFE_COATS: Readonly<Record<WildlifeSpecies, AnimalCoat>> = {
  hare: { coat: 0x8d7554, belly: 0xd7cbb4, horn: 0x6b563c, face: 0x4b3b2a },
  deer: { coat: 0xa25a30, belly: 0xece1cd, horn: 0x5c4632, face: 0x3d2c1e },
  chamois: { coat: 0x3a2c20, belly: 0x8a7a63, horn: 0x1a1511, face: 0xd6c9ae },
};

/**
 * The birds a spooked deer puts up: nearly black and unlit — a moving hole in
 * the light, noticed from across a field.
 */
export const BIRD_COLOR = 0x1e1a17;

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

const tmpHSL = { h: 0, s: 0, l: 0 };

/**
 * Writes `base` into `out` with a deterministic HSL nudge from `seed`/`salt`.
 */
export function jitterColor(
  out: THREE.Color,
  base: THREE.Color,
  amp: Jitter,
  seed: number,
  salt = 0,
): THREE.Color {
  base.getHSL(tmpHSL);
  const h = (tmpHSL.h + randSym(seed, salt) * amp.h + 1) % 1;
  const s = clamp01(tmpHSL.s + randSym(seed, salt + 101) * amp.s);
  const l = clamp01(tmpHSL.l + randSym(seed, salt + 202) * amp.l);
  return out.setHSL(h, s, l);
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Fake-AO factor for a side-face vertex at world height `y`. */
export function groundDarkening(y: number): number {
  if (y >= AO_GROUND_HEIGHT) return 1;
  const t = clamp01(y / AO_GROUND_HEIGHT);
  return AO_GROUND_FACTOR + (1 - AO_GROUND_FACTOR) * t;
}
